// =============================================================================
// src/features/analytics/analytics-shell-markup.ts - Sidebar e toolbar Analytics.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import { resolveOwnerIdentity } from '../../app/owner-identity';
import {
  OMAR_ANALYTICS_MODULES,
  OMAR_ANALYTICS_PERIOD_LABELS,
  type OmarAnalyticsModuleEntry,
  type OmarAnalyticsModuleGroup,
} from './analytics-data';
import { getAnalyticsViewState } from './analytics-view-state';

/** Renderiza a navegacao interna do Analytics. */
export function buildAnalyticsSidebarMarkup(): string {
  const ownerIdentity = resolveOwnerIdentity();
  const projectInitials = analyticsProjectInitials(ownerIdentity.name);
  return `<aside class="u-analytics-sidebar" aria-label="Navegacao Analytics">
    <button class="u-analytics-collapse-btn" type="button" data-analytics-toggle-panel="left" aria-label="Recolher menu esquerdo"><i class="ti ti-layout-sidebar-left-collapse"></i></button>
    <div class="u-analytics-project"><span class="u-analytics-project-dot"></span><span class="u-analytics-project-compact" aria-hidden="true">${escapeHtml(projectInitials)}</span><div><b>${escapeHtml(ownerIdentity.name)}</b><span>Amazonas - Manaus critica</span></div></div>
    <nav class="u-analytics-nav">${analyticsModuleGroupsMarkup()}</nav>
  </aside>`;
}

/** Renderiza a toolbar compacta compartilhada pelos modulos. */
export function buildAnalyticsToolbarMarkup(): string {
  return `<header class="u-analytics-toolbar">
    <label class="u-analytics-search"><i class="ti ti-search" aria-hidden="true"></i><input id="analyticsSearchInput" type="search" placeholder="Buscar mencoes, canais e temas" autocomplete="off"></label>
    ${analyticsPeriodsMarkup()}
    <button type="button" class="u-analytics-ai-filter" disabled title="Disponivel na proxima fase"><i class="ti ti-sparkles" aria-hidden="true"></i> Filtrar com IA</button>
    <button type="button" class="u-analytics-mobile-filter" data-analytics-filter-drawer-open><i class="ti ti-adjustments-horizontal" aria-hidden="true"></i> Filtros</button>
    <button type="button" class="u-analytics-export" id="analyticsExportBtn"><i class="ti ti-download" aria-hidden="true"></i> Exportar leitura</button>
  </header>`;
}

function analyticsModuleGroupsMarkup(): string {
  const groupNames: readonly OmarAnalyticsModuleGroup[] = ['monitoring', 'output', 'intelligence'];
  return groupNames.map(analyticsModuleGroupMarkup).join('');
}

function analyticsModuleGroupMarkup(groupName: OmarAnalyticsModuleGroup): string {
  const groupModules = OMAR_ANALYTICS_MODULES.filter((analyticsModule) => analyticsModule.groupName === groupName);
  return `<div class="u-analytics-nav-group"><span class="u-analytics-nav-title">${analyticsGroupLabel(groupName)}</span>${groupModules.map(analyticsSideButtonMarkup).join('')}</div>`;
}

// Secoes DESATIVADAS (2026-07-25, pedido do usuario: "remover os conteudos
// mockados"): so mock, sem dado real da base ainda — botao visivel porem
// inerte, padrao da doca de inteligencia. Sites/Influenciadores tem tabela
// live mas o usuario pediu para desligar junto. Para religar: tirar o nome
// deste conjunto. 'digest' RELIGADO em 2026-07-27: a aba Resumo de IA passou
// a exibir os Cards de Inteligencia Tatica reais (analytics-insights-*.ts).
const ANALYTICS_DISABLED_MODULES: ReadonlySet<OmarAnalyticsModuleEntry['moduleName']> = new Set([
  'influencers', 'sites', 'emails', 'assistant', 'comparison', 'team',
]);

function analyticsSideButtonMarkup(analyticsModule: OmarAnalyticsModuleEntry): string {
  const activeClass = analyticsModule.moduleName === 'mentions' ? ' is-active' : '';
  const pressedState = analyticsModule.moduleName === 'mentions' ? 'true' : 'false';
  const disabledAttrs = ANALYTICS_DISABLED_MODULES.has(analyticsModule.moduleName)
    ? ' disabled aria-disabled="true"'
    : '';
  const buttonTitle = ANALYTICS_DISABLED_MODULES.has(analyticsModule.moduleName)
    ? 'Disponivel quando houver dado real da base'
    : analyticsModule.label;
  return `<button class="u-analytics-side-btn${activeClass}" type="button"${disabledAttrs} aria-pressed="${pressedState}" data-analytics-module="${analyticsModule.moduleName}" title="${escapeHtml(buttonTitle)}"><i class="ti ${analyticsModule.iconName}"></i><span>${escapeHtml(analyticsModule.label)}</span></button>`;
}

function analyticsGroupLabel(groupName: OmarAnalyticsModuleGroup): string {
  if (groupName === 'monitoring') return 'Monitoramento';
  if (groupName === 'output') return 'Entrega';
  return 'Inteligencia';
}

function analyticsProjectInitials(ownerName: string): string {
  const compactOwnerName = ownerName.replace(/^Sen\.\s+/i, '').trim();
  const ownerNameParts = compactOwnerName.split(/\s+/).filter(Boolean);
  const compactInitials = ownerNameParts.map((namePart) => namePart.charAt(0)).slice(0, 2).join('');
  return compactInitials.toUpperCase();
}

/** Periodo ativo = o que os numeros mostram (2026-09-02: era "7 dias" pressionado
 * com numeros de 30 dias). O recorte vem da view-state, persistido por visitante. */
function analyticsPeriodsMarkup(): string {
  const activePeriod = getAnalyticsViewState().period;
  const periodButtons = (['last7', 'last30'] as const)
    .map((period) => `<button type="button" class="u-analytics-period${period === activePeriod ? ' is-active' : ''}" aria-pressed="${period === activePeriod ? 'true' : 'false'}" data-analytics-period="${period}">${OMAR_ANALYTICS_PERIOD_LABELS[period]}</button>`)
    .join('\n    ');
  return `<div class="u-analytics-periods" role="group" aria-label="Periodo">
    ${periodButtons}
  </div>`;
}
